import type { Howl } from 'howler'
import { log, reason } from '../app/log.js'
import { setMeta } from '../state/db.js'

/**
 * ARCHITECTURE §10. One AudioContext, unlocked by the first user gesture (the profile pick tap
 * in Mini, CONVENTIONS §8). Voice lines are lazy per screen, music is one loop at a time.
 */

const logger = log('audio')

type TrackId = string
type HowlerModule = typeof import('howler')

/**
 * howler arrives with the first unlock gesture, never with the app shell (ARCHITECTURE §9,
 * PERF-05): every export below is a no op until then, which is also what the browser rule
 * wants, since nothing may sound before a gesture anyway.
 */
let howler: HowlerModule | null = null
const volume = 1

const sfxCache = new Map<string, Howl>()
const voiceCache = new Map<string, Howl>()
let music: Howl | null = null
let musicId: TrackId | null = null
let unlocked = false
let muted = false

/** Call from a real user gesture. Browsers refuse to start audio any other way. */
export async function unlock(): Promise<boolean> {
  if (unlocked) return true
  try {
    if (!howler) {
      howler = await import('howler')
      howler.Howler.mute(muted)
      howler.Howler.volume(volume)
    }
    const { Howler } = howler
    const context = Howler.ctx
    if (context && context.state === 'suspended') await context.resume()
    unlocked = Howler.ctx ? Howler.ctx.state === 'running' : true
    if (unlocked) await setMeta('audioUnlocked', true)
    return unlocked
  } catch (error) {
    logger.warn('could not unlock the audio context', reason(error))
    return false
  }
}

export function setMuted(next: boolean): void {
  muted = next
  howler?.Howler.mute(next)
}

export function isMuted(): boolean {
  return muted
}

function load(cache: Map<string, Howl>, id: string, src: string[], loop = false): Howl | null {
  const existing = cache.get(id)
  if (existing) return existing
  if (!howler) return null
  const howl = new howler.Howl({
    src,
    loop,
    preload: true,
    html5: false,
    onloaderror: (_id, error) => logger.warn(`could not load ${id}`, error),
  })
  cache.set(id, howl)
  return howl
}

/** A short effect. `content/sfx.json` maps ids to files once the art lane lands them. */
export function sfx(id: string): void {
  if (!unlocked) return
  load(sfxCache, id, [`/sfx/${id}.ogg`, `/sfx/${id}.m4a`])?.play()
}

/** A voice line. CONTENT §7 ids, files under public/voice. */
export function voice(lineId: string): Promise<void> {
  if (!unlocked) return Promise.resolve()
  const howl = load(voiceCache, lineId, [`/voice/${lineId}.mp3`])
  if (!howl) return Promise.resolve()
  return new Promise((resolve) => {
    howl.once('end', () => resolve())
    howl.once('loaderror', () => resolve())
    howl.once('playerror', () => resolve())
    howl.play()
  })
}

export function stopVoice(): void {
  for (const howl of voiceCache.values()) howl.stop()
}

export function playMusic(trackId: TrackId | null): void {
  if (musicId === trackId) return
  music?.stop()
  music = null
  musicId = trackId
  if (!trackId || !unlocked || !howler) return
  music = new howler.Howl({
    src: [`/music/${trackId}.ogg`, `/music/${trackId}.m4a`],
    loop: true,
    volume: 0.5,
  })
  music.play()
}

/** Drops the music bed while a voice line plays, then brings it back. */
export function duck(during: Promise<void>): Promise<void> {
  if (!music) return during
  music.volume(0.15)
  return during.finally(() => music?.volume(0.5))
}

/** Tests and the ritual teardown need everything to stop at once. */
export function stopAll(): void {
  stopVoice()
  for (const howl of sfxCache.values()) howl.stop()
  playMusic(null)
}
